import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Radio, Typography, Switch, Alert, Divider, message, Space, Spin, Tag, Modal, Select, Table, Tooltip, Popconfirm, Badge, Tabs, Row, Col } from 'antd';
import { SettingOutlined, KeyOutlined, GlobalOutlined, MessageOutlined, CheckCircleFilled, SyncOutlined, InfoCircleOutlined, PlusOutlined, DeleteOutlined, SendOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, EditOutlined, RobotOutlined, CodeOutlined, ShopOutlined, BulbOutlined } from '@ant-design/icons';
import apiClient, { API_ORIGIN, API_BASE } from '../api';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || API_ORIGIN;

const { Title, Text, Paragraph } = Typography;

const AccountManager = () => {
  const [form] = Form.useForm();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [testForm] = Form.useForm();
  const [remoteTemplates, setRemoteTemplates] = useState([]);
  const [editingNumber, setEditingNumber] = useState(null);
  const [testNumber, setTestNumber] = useState(null);
  const [testLoading, setTestLoading] = useState({});
  const [discovering, setDiscovering] = useState(false);
  const [discoveredNumbers, setDiscoveredNumbers] = useState([]);
  const [addForm] = Form.useForm();

  const [qrCode, setQrCode] = useState(null);
  const [webjsStatus, setWebjsStatus] = useState('unconnected');

  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');

  // Business profile state — synced from account on load
  const [bizProfile, setBizProfile] = useState({});
  const [bizProducts, setBizProducts] = useState([]);
  const [savingBiz, setSavingBiz] = useState(false);

  const UNIT_OPTIONS = [
    { value: 'per pack',    label: 'per pack' },
    { value: 'per kg',      label: 'per kg' },
    { value: 'per 500g',    label: 'per 500g' },
    { value: 'per piece',   label: 'per piece' },
    { value: 'per litre',   label: 'per litre' },
    { value: 'per month',   label: 'per month' },
    { value: 'per year',    label: 'per year' },
    { value: 'per session', label: 'per session' },
    { value: 'per visit',   label: 'per visit' },
    { value: 'per person',  label: 'per person' },
    { value: 'per night',   label: 'per night' },
    { value: 'per sq ft',   label: 'per sq ft' },
    { value: 'custom',      label: 'custom…' },
  ];

  useEffect(() => {
    loadAccount();

    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('Connected to socket for QR updates');
    });

    socket.on('qrcode', (data) => {
      // data is { accountId, qrDataURL }
      if (account && data.accountId === account._id) {
        setQrCode(data.qrDataURL);
        setWebjsStatus('unconnected');
      }
    });

    socket.on('ready', (data) => {
      if (account && data.accountId === account._id) {
        setWebjsStatus('ready');
        setQrCode(null);
      }
    });

    socket.on('authenticated', (data) => {
      if (account && data.accountId === account._id) {
        setWebjsStatus('authenticated');
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [account?._id]);

  const loadAccount = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/accounts/me');
      if (res.data.success) {
        const acct = res.data.account;
        setAccount(acct);
        if (res.data.webhookVerifyToken) setWebhookVerifyToken(res.data.webhookVerifyToken);
        form.setFieldsValue({
          name: acct.name,
          email: acct.email,
          whatsappProvider: acct.whatsappProvider || 'webjs',
          autoReengagement: acct.autoReengagement ?? true,
          handoverEnabled: acct.handoverSettings?.enabled ?? true,
          aiTakebackMinutes: acct.handoverSettings?.aiTakebackMinutes ?? 30,
          autoResumeAIAfterHumanReply: acct.handoverSettings?.autoResumeAIAfterHumanReply ?? false
        });
        // Populate business profile panel
        setBizProfile({
          businessType:        acct.businessType        || '',
          businessDescription: acct.businessDescription || '',
          brandTone:           acct.brandTone           || 'friendly',
          commandCentreGoal:   acct.commandCentreGoal   || '',
          commandCentreActions: acct.commandCentreActions || [],
        });
        setBizProducts(acct.products?.length ? acct.products : [{ name: '', description: '', price: '', currency: '₹', unit: 'per pack' }]);
      }
    } catch (error) {
      message.error('Failed to load account settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccount = async (updatedFields) => {
    setSaving(true);
    try {
      // Prepare payload with nested handoverSettings
      const payload = { ...updatedFields };
      if (updatedFields.handoverEnabled !== undefined || updatedFields.aiTakebackMinutes !== undefined || updatedFields.autoResumeAIAfterHumanReply !== undefined) {
        payload.handoverSettings = {
          enabled: updatedFields.handoverEnabled ?? account.handoverSettings?.enabled,
          aiTakebackMinutes: updatedFields.aiTakebackMinutes ?? account.handoverSettings?.aiTakebackMinutes,
          autoResumeAIAfterHumanReply: updatedFields.autoResumeAIAfterHumanReply ?? account.handoverSettings?.autoResumeAIAfterHumanReply
        };
        // Remove flattened fields to avoid confusion
        delete payload.handoverEnabled;
        delete payload.aiTakebackMinutes;
        delete payload.autoResumeAIAfterHumanReply;
      }

      const res = await apiClient.put('/accounts/config', payload);
      if (res.data.success) {
        setAccount(res.data.account);
        message.success('Settings updated');
        return true;
      }
    } catch (error) {
      message.error('Update failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
    return false;
  };

  const handleToggleNumber = async (numberId, active) => {
    const updatedNumbers = account.whatsappNumbers.map(n => 
      n.phoneNumberId === numberId ? { ...n, isActive: active } : n
    );
    await handleUpdateAccount({ whatsappNumbers: updatedNumbers });
  };

  const handleDeleteNumber = async (numberId) => {
    const updatedNumbers = account.whatsappNumbers.filter(n => n.phoneNumberId !== numberId);
    await handleUpdateAccount({ whatsappNumbers: updatedNumbers });
  };

  const handleResetConfig = async () => {
    Modal.confirm({
      title: 'Reset WhatsApp Integration?',
      icon: <ExclamationCircleOutlined />,
      content: 'This will wipe all your connected Cloud API numbers and reset your provider to WhatsApp Web JS. This action cannot be undone.',
      okText: 'Yes, Reset Everything',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setSaving(true);
        try {
          const res = await apiClient.post('/accounts/reset-config');
          if (res.data.success) {
            setAccount(res.data.account);
            form.setFieldsValue({
              whatsappProvider: 'webjs'
            });
            message.success('Official account reset successfully');
          }
        } catch (error) {
          message.error('Reset failed: ' + (error.response?.data?.message || error.message));
        } finally {
          setSaving(false);
        }
      },
    });
  };


  const handleDiscoverNumbers = async () => {
    const token = addForm.getFieldValue('accessToken');
    if (!token) return message.error('Paste your Access Token first');

    setDiscovering(true);
    setDiscoveredNumbers([]);
    try {
      const res = await apiClient.post('/whatsapp/discover-from-token', { accessToken: token });
      if (res.data.success && res.data.numbers?.length > 0) {
        setDiscoveredNumbers(res.data.numbers);
        message.success(`Found ${res.data.numbers.length} numbers!`);
      } else {
        message.warning('No numbers found. Check your token permissions.');
      }
    } catch (err) {
      message.error('Discovery failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setDiscovering(false);
    }
  };

  const handleSelectDiscovered = (wabaId) => {
    const selected = discoveredNumbers.find(n => n.wabaId === wabaId);
    if (selected) {
      addForm.setFieldsValue({
        phoneNumberId: selected.phoneNumberId,
        wabaId: selected.wabaId,
        displayNumber: selected.verifiedName || selected.displayNumber
      });
    }
  };

  const handleOpenTestModal = async (numberRecord) => {
    setTestNumber(numberRecord);
    setTestModalVisible(true);
    setRemoteTemplates([]);
    
    // WABA ID Fallback: handle both new (wabaId) and old (businessAccountId) field names
    const wabaIdToUse = numberRecord.wabaId || numberRecord.businessAccountId || (account.cloudApiConfig?.businessAccountId);
    
    if (!wabaIdToUse) {
      console.error('No WABA ID found for this number');
      message.error('Missing WABA ID for this number');
      return;
    }

    try {
      const res = await apiClient.get(`/whatsapp/remote-templates?wabaId=${wabaIdToUse}`);
      if (res.data.success) {
        setRemoteTemplates(res.data.templates);
      } else {
        message.warn(res.data.message || 'No templates found');
      }
    } catch (err) {
      console.warn('Could not fetch templates for testing:', err.message);
    }
  };

  const handleSendAdvancedTest = async (values) => {
    const { recipient, type, templateName, variables } = values;
    const numberId = testNumber.phoneNumberId;
    
    const selectedTpl = remoteTemplates.find(t => t.name === templateName);
    const language = selectedTpl ? selectedTpl.language : 'en_US';

    setTestLoading(prev => ({ ...prev, [numberId]: true }));
    try {
      const payload = { 
        phoneNumberId: numberId, 
        recipient,
        type,
        templateName,
        language
      };

      // Construct components if variables exist
      if (type === 'template' && variables) {
        const parameters = Object.keys(variables)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(idx => ({
            type: 'text',
            text: variables[idx]
          }));

        if (parameters.length > 0) {
          payload.components = [{
            type: 'body',
            parameters: parameters
          }];
        }
      }

      const res = await apiClient.post('/whatsapp/test-send', payload);
      if (res.data.success) {
        message.success(`Test ${type} message sent!`);
        setTestModalVisible(false);
        testForm.resetFields();
      }
    } catch (e) {
      message.error('Test failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setTestLoading(prev => ({ ...prev, [numberId]: false }));
    }
  };

  const showAddModal = () => {
    setEditingNumber(null);
    setModalVisible(true);
  };

  const handleModalOk = async (values) => {
    let updatedNumbers = [...(account.whatsappNumbers || [])];
    if (editingNumber) {
      updatedNumbers = updatedNumbers.map(n => n.phoneNumberId === editingNumber.phoneNumberId ? values : n);
    } else {
      // Check for duplicates
      if (updatedNumbers.find(n => n.phoneNumberId === values.phoneNumberId)) {
        return message.error('This Phone Number ID is already added.');
      }
      updatedNumbers.push({ ...values, isActive: true });
    }
    
    const success = await handleUpdateAccount({ whatsappNumbers: updatedNumbers });
    if (success) setModalVisible(false);
  };

  const saveBizProfile = async () => {
    setSavingBiz(true);
    try {
      const res = await apiClient.put('/accounts/config', {
        ...bizProfile,
        products: bizProducts.filter(p => p.name.trim()),
      });
      if (res.data.success) {
        setAccount(res.data.account);
        message.success('Business profile saved');
      }
    } catch (err) {
      message.error('Save failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setSavingBiz(false);
    }
  };

  const updateBizProduct = (i, field, val) =>
    setBizProducts(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  const addBizProduct = () =>
    setBizProducts(prev => [...prev, { name: '', description: '', price: '', currency: '₹', unit: 'per pack' }]);

  const removeBizProduct = (i) =>
    setBizProducts(prev => prev.filter((_, idx) => idx !== i));

  const columns = [
    {
      title: 'Display Number',
      key: 'display',
      render: (_, record) => (
        <Space>
          <Text strong>{record.displayNumber || 'Unnamed'}</Text>
          {record.isRegistered ? <Tag color="success">Registered</Tag> : <Tag color="warning">Not Registered</Tag>}
        </Space>
      )
    },
    {
      title: 'IDs',
      key: 'ids',
      render: (_, record) => (
        <div style={{ fontSize: '11px' }}>
          <Text type="secondary">Phone: {record.phoneNumberId}</Text><br/>
          <Text type="secondary">WABA: {record.wabaId}</Text>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Switch 
          checked={record.isActive} 
          size="small" 
          onChange={(checked) => handleToggleNumber(record.phoneNumberId, checked)}
        />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Send Test Message">
            <Button 
              size="small" 
              icon={<SendOutlined />} 
              onClick={() => handleOpenTestModal(record)}
              loading={testLoading[record.phoneNumberId]}
            />
          </Tooltip>
           <Button 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => {
              setEditingNumber(record);
              setModalVisible(true);
            }}
          >Edit Official Config</Button>
          <Popconfirm title="Delete this number?" onConfirm={() => handleDeleteNumber(record.phoneNumberId)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;

  const items = [
    {
      key: '1',
      label: <span><SettingOutlined /> Account Settings</span>,
      children: (
        <Form 
          form={form} 
          layout="vertical" 
          onValuesChange={(_, allValues) => handleUpdateAccount(allValues)}
        >
          <Card 
            title={<Space><KeyOutlined /> Identity & Products</Space>} 
            size="small" 
            style={{ marginBottom: 24, border: '1px solid #d9d9d9' }}
          >
            <Form.Item name="name" label="Business Name" rules={[{ required: true }]}>
              <Input placeholder="Business Name" />
            </Form.Item>
            
            <Divider style={{ margin: '16px 0' }} />
            <Title level={5}>Product Catalog (For AI Context)</Title>
            <Paragraph type="secondary" style={{ fontSize: '12px' }}>
              Products here are automatically recommended by the AI during chat.
            </Paragraph>
            <Table
              dataSource={account?.products || []}
              size="small"
              pagination={false}
              rowKey={(record, index) => index}
              columns={[
                {
                  title: 'Name',
                  dataIndex: 'name',
                  render: (val, record, index) => (
                    <Input 
                      value={val} 
                      size="small"
                      onChange={(e) => {
                        const updated = [...account.products];
                        updated[index].name = e.target.value;
                        setAccount({ ...account, products: updated });
                      }}
                      onBlur={() => handleUpdateAccount({ products: account.products })}
                    />
                  )
                },
                {
                  title: 'Price',
                  dataIndex: 'price',
                  width: 80,
                  render: (val, record, index) => (
                    <Input 
                      type="number"
                      value={val} 
                      size="small"
                      onChange={(e) => {
                        const updated = [...account.products];
                        updated[index].price = parseFloat(e.target.value) || 0;
                        setAccount({ ...account, products: updated });
                      }}
                      onBlur={() => handleUpdateAccount({ products: account.products })}
                    />
                  )
                },
                {
                  title: '',
                  key: 'actions',
                  width: 40,
                  render: (_, record, index) => (
                    <Button 
                      type="text" 
                      danger 
                      size="small"
                      icon={<DeleteOutlined />} 
                      onClick={() => {
                        const updated = account.products.filter((_, i) => i !== index);
                        handleUpdateAccount({ products: updated });
                      }}
                    />
                  )
                }
              ]}
            />
            <Button 
              type="dashed" 
              block 
              icon={<PlusOutlined />} 
              size="small" 
              style={{ marginTop: 8 }}
              onClick={() => {
                const updated = [...(account.products || []), { name: 'New Product', price: 0, isActive: true }];
                handleUpdateAccount({ products: updated });
              }}
            >
              Add Product
            </Button>
          </Card>

          <Card title={<Space><SyncOutlined /> Automation & Takeback</Space>} size="small" style={{ border: '1px solid #6366f1' }}>
            <Form.Item name="autoReengagement" label="Autonomous Re-engagement (24h Meta Templates)" valuePropName="checked">
              <Switch checkedChildren="ON" unCheckedChildren="OFF" />
            </Form.Item>
            <Divider plain>Human-AI Handoff</Divider>
            <Form.Item name="handoverEnabled" label="Silence Recovery Takeback" valuePropName="checked">
              <Switch checkedChildren="ENABLED" unCheckedChildren="DISABLED" />
            </Form.Item>
            <Form.Item name="aiTakebackMinutes" label="Silence Timeout (Minutes)">
              <Input type="number" addonAfter="min" />
            </Form.Item>
            <Form.Item name="autoResumeAIAfterHumanReply" label="Auto-enable AI after Agent reply" valuePropName="checked">
              <Switch checkedChildren="AUTO-ENABLE" unCheckedChildren="STAY MANUAL" />
            </Form.Item>
          </Card>
        </Form>
      )
    },
    {
      key: 'biz',
      label: <span><ShopOutlined /> Business Profile</span>,
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {account?.onboardingCompleted
            ? <Alert type="success" showIcon message="Onboarding complete — your AI team is live." style={{ marginBottom: 4 }} />
            : <Alert type="warning" showIcon message="Onboarding not completed yet. Fill in the details below and run the Onboarding wizard to generate your agent team." style={{ marginBottom: 4 }} />
          }

          {/* Business Identity */}
          <Card title={<Space><ShopOutlined /> Business Identity</Space>} size="small">
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Business Type</div>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Select type"
                    value={bizProfile.businessType || undefined}
                    onChange={v => setBizProfile(p => ({ ...p, businessType: v }))}
                    options={[
                      { value: 'd2c',        label: 'D2C / E-commerce' },
                      { value: 'hotel',      label: 'Hotel / Hospitality' },
                      { value: 'saas',       label: 'SaaS / Software' },
                      { value: 'clinic',     label: 'Clinic / Healthcare' },
                      { value: 'legal',      label: 'Legal Services' },
                      { value: 'restaurant', label: 'Restaurant / Food' },
                      { value: 'education',  label: 'Education' },
                      { value: 'realestate', label: 'Real Estate' },
                      { value: 'default',    label: 'Other' },
                    ]}
                  />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Brand Tone</div>
                  <Select
                    style={{ width: '100%' }}
                    value={bizProfile.brandTone || 'friendly'}
                    onChange={v => setBizProfile(p => ({ ...p, brandTone: v }))}
                    options={[
                      { value: 'friendly',     label: '😊 Friendly — warm, conversational' },
                      { value: 'professional', label: '💼 Professional — clear, polished' },
                      { value: 'luxury',       label: '✨ Luxury — refined, elegant' },
                      { value: 'playful',      label: '🎉 Playful — fun, energetic' },
                    ]}
                  />
                </div>
              </Col>
            </Row>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Business Description <span style={{ color: '#aaa' }}>(the AI reads this to understand your brand)</span></div>
              <Input.TextArea
                rows={3}
                placeholder="Describe your business — what you sell, who your customers are, what makes you different…"
                value={bizProfile.businessDescription}
                onChange={e => setBizProfile(p => ({ ...p, businessDescription: e.target.value }))}
              />
            </div>
          </Card>

          {/* Command Centre */}
          <Card title={<Space><BulbOutlined /> Command Centre Intelligence</Space>} size="small">
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Business Goal <span style={{ color: '#aaa' }}>(used to generate proactive campaign recommendations)</span></div>
              <Input
                placeholder="e.g. Increase repeat purchases and grow subscription revenue"
                value={bizProfile.commandCentreGoal}
                onChange={e => setBizProfile(p => ({ ...p, commandCentreGoal: e.target.value }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Available Actions <span style={{ color: '#aaa' }}>(what actions can the Command Centre recommend?)</span></div>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Type and press Enter to add actions…"
                value={bizProfile.commandCentreActions || []}
                onChange={v => setBizProfile(p => ({ ...p, commandCentreActions: v }))}
                options={[
                  { value: 'discount offer' },
                  { value: 'product recommendation' },
                  { value: 'free sample' },
                  { value: 'subscription reminder' },
                  { value: 'follow-up message' },
                  { value: 'feedback request' },
                  { value: 'loyalty reward' },
                  { value: 'referral nudge' },
                  { value: 'reorder reminder' },
                  { value: 'appointment reminder' },
                ]}
              />
            </div>
          </Card>

          {/* Products */}
          <Card
            title={<Space><ShopOutlined /> Products & Services</Space>}
            size="small"
            extra={<Button size="small" icon={<PlusOutlined />} onClick={addBizProduct}>Add</Button>}
          >
            <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              These are injected into every agent's context — the AI mentions them naturally in conversations.
            </div>
            {bizProducts.map((p, i) => (
              <Card key={i} size="small" style={{ marginBottom: 8, background: '#fafafa' }} bodyStyle={{ padding: '8px 12px' }}>
                <Row gutter={8} align="middle">
                  <Col xs={24} sm={6}>
                    <Input
                      size="small"
                      placeholder="Product name *"
                      value={p.name}
                      onChange={e => updateBizProduct(i, 'name', e.target.value)}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Input
                      size="small"
                      placeholder="Short description"
                      value={p.description}
                      onChange={e => updateBizProduct(i, 'description', e.target.value)}
                    />
                  </Col>
                  <Col xs={12} sm={4}>
                    <Input
                      size="small"
                      placeholder="Price"
                      value={p.price}
                      onChange={e => updateBizProduct(i, 'price', e.target.value)}
                      prefix={p.currency || '₹'}
                    />
                  </Col>
                  <Col xs={10} sm={5}>
                    <Select
                      size="small"
                      style={{ width: '100%' }}
                      value={p.unit || 'per pack'}
                      onChange={v => updateBizProduct(i, 'unit', v)}
                      dropdownMatchSelectWidth={false}
                      options={UNIT_OPTIONS}
                    />
                    {p.unit === 'custom' && (
                      <Input
                        size="small"
                        placeholder="e.g. per 2kg bag"
                        style={{ marginTop: 4 }}
                        value={p.customUnit || ''}
                        onChange={e => updateBizProduct(i, 'customUnit', e.target.value)}
                      />
                    )}
                  </Col>
                  <Col xs={2} sm={1}>
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeBizProduct(i)}
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </Card>

          <Button type="primary" loading={savingBiz} onClick={saveBizProfile} style={{ alignSelf: 'flex-start' }}>
            Save Business Profile
          </Button>
        </div>
      ),
    },
    {
      key: '2',
      label: <span><GlobalOutlined /> WhatsApp Integration</span>,
      children: (
        <div>
          <Card title="Official WhatsApp Cloud API" size="small" style={{ marginBottom: 24 }}>
            <div style={{ background: '#e6f7ff', padding: '16px', borderRadius: 8, border: '1px solid #91d5ff', marginBottom: 24 }}>
              <Title level={5} style={{ marginTop: 0 }}><GlobalOutlined /> Meta Webhook Setup</Title>
              <Paragraph style={{ fontSize: '13px', marginBottom: 12 }}>
                Paste both values into your Meta Developer Dashboard → WhatsApp → Configuration:
              </Paragraph>

              {/* Callback URL */}
              <div style={{ marginBottom: 10 }}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>CALLBACK URL</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                  <code style={{ fontSize: '13px', flex: 1, wordBreak: 'break-all' }}>
                    {API_ORIGIN}/webhooks/wa/{account?._id}
                  </code>
                  <Button type="link" size="small" style={{ flexShrink: 0 }} onClick={() => {
                    navigator.clipboard.writeText(`${API_ORIGIN}/webhooks/wa/${account?._id}`);
                    message.success('URL copied');
                  }}>Copy</Button>
                </div>
              </div>

              {/* Verify Token */}
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>VERIFY TOKEN</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 4 }}>
                  <code style={{ fontSize: '13px', flex: 1 }}>
                    {webhookVerifyToken || '—'}
                  </code>
                  <Button type="link" size="small" style={{ flexShrink: 0 }} onClick={() => {
                    navigator.clipboard.writeText(webhookVerifyToken);
                    message.success('Verify token copied');
                  }}>Copy</Button>
                </div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                  Set <code>WEBHOOK_VERIFY_TOKEN</code> in your server <code>.env</code> to change this.
                </Text>
              </div>
            </div>

            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Title level={4} style={{ margin: 0 }}><MessageOutlined /> Connected Numbers</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>Connect New Number</Button>
            </div>
            
            <Table 
              dataSource={account?.whatsappNumbers || []} 
              columns={columns} 
              rowKey="phoneNumberId" 
              pagination={false}
              size="small"
            />
          </Card>
          
          <Alert
            message="Legacy Mode (WebJS)"
            description={
              <div>
                <Paragraph style={{ margin: 0 }}>
                  Toggle your account to WebJS if you wish to use a standard WhatsApp log-in via QR code. 
                  Note that many advanced multi-number features are disabled in this mode.
                </Paragraph>
                
                {account?.whatsappProvider === 'webjs' && (
                  <div style={{ marginTop: 20, textAlign: 'center', background: '#fff', padding: 20, borderRadius: 8, border: '1px border #d9d9d9' }}>
                    {webjsStatus === 'ready' ? (
                      <div>
                        <CheckCircleFilled style={{ fontSize: 48, color: '#52c41a' }} />
                        <Title level={4} style={{ marginTop: 16 }}>WhatsApp Connected</Title>
                        <Text type="secondary">Bot is live on your personal WhatsApp account.</Text>
                      </div>
                    ) : (
                      <div>
                        {qrCode ? (
                          <>
                            <Title level={4}>Scan QR Code</Title>
                            <img src={qrCode} alt="WhatsApp QR" style={{ width: 250, height: 250, border: '1px solid #f0f0f0' }} />
                            <br />
                            <Text type="secondary">Open WhatsApp on your phone &gt; Linked Devices &gt; Link a Device</Text>
                          </>
                        ) : (
                          <div style={{ padding: '40px 0' }}>
                            <Spin tip="Waiting for QR code..." />
                            <br /><br />
                            <Text type="secondary">Starting browser instance... (May take 30-60s)</Text>
                          </div>
                        )}
                        <div style={{ marginTop: 16 }}>
                          <Tag icon={<SyncOutlined spin />} color="processing">Status: {webjsStatus}</Tag>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            }
            type={account?.whatsappProvider === 'webjs' ? "success" : "info"}
            showIcon
            action={
              <Form form={form} layout="inline">
                <Form.Item name="whatsappProvider">
                   <Radio.Group size="small" buttonStyle="solid" onChange={(e) => handleUpdateAccount({ whatsappProvider: e.target.value })}>
                     <Radio.Button value="webjs">WEBJS</Radio.Button>
                     <Radio.Button value="cloud_api">CLOUD API</Radio.Button>
                   </Radio.Group>
                </Form.Item>
              </Form>
            }
          />
        </div>
      )
    },
    {
      key: '3',
      label: <span><CodeOutlined /> CRM & Developer Integration</span>,
      children: (
        <div style={{ gap: '24px', display: 'flex', flexDirection: 'column' }}>
           <Card 
            title={<Space><KeyOutlined /> API Credentials</Space>} 
            size="small" 
            style={{ border: '1px solid #d9d9d9' }}
            extra={<Tag color="green">PRODUCTION</Tag>}
          >
            <Paragraph>Use this secret key to authenticate your CRM with the Ombrella Messaging Engine.</Paragraph>
            <Text type="secondary" style={{ fontSize: '11px' }}>X-API-KEY (HEADER)</Text>
            <Input.Password 
              readOnly 
              value={account?.apiKey} 
              style={{ fontFamily: 'monospace', marginBottom: 12 }}
              suffix={
                <Button type="link" size="small" onClick={() => {
                  navigator.clipboard.writeText(account?.apiKey);
                  message.success('API Key copied');
                }}>Copy Key</Button>
              }
            />
            <Alert type="warning" showIcon message="Never share this key in public frontend code." />
          </Card>

          <Card title={<Space><SendOutlined /> Inbound Handoff Webhook (To your CRM)</Space>} size="small">
             <Paragraph type="secondary">
                When an AI agent triggers a handoff, the customer's message will be POSTed to this URL.
             </Paragraph>
             <Form layout="vertical">
                <Form.Item label={<b>Your CRM Endpoint URL</b>}>
                   <Input 
                      placeholder="https://your-crm.com/webhooks/whatsapp" 
                      defaultValue={account?.handoverSettings?.webhookUrl}
                      prefix={<GlobalOutlined />}
                      onBlur={(e) => handleUpdateAccount({ handoverSettings: { ...account.handoverSettings, webhookUrl: e.target.value }})}
                    />
                </Form.Item>
             </Form>
             <Text type="secondary" style={{ fontSize: '12px' }}>
                <b>Payload Format:</b> <code>{`{ from: "9188...", body: "Hello", fromPhoneNumberId: "..." }`}</code>
             </Text>
          </Card>

          <Card title={<Space><InfoCircleOutlined /> Integration Code Samples</Space>} size="small">
             <Tabs size="small" items={[
               {
                 key: 'curl',
                 label: 'CURL (Reply)',
                 children: (
                   <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, color: '#f8fafc', fontSize: '12px' }}>
                     <pre style={{ marginBottom: 0 }}>{`curl -X POST "${API_ORIGIN}/api/whatsapp/message" \\
  -H "X-API-Key: ${account?.apiKey?.substring(0, 5)}..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "918840824279",
    "message": "Hello from my CRM!",
    "fromPhoneNumberId": "9974945067..."
  }'`}</pre>
                   </div>
                 )
               },
               {
                 key: 'close',
                 label: 'CLOSE SESSION (Return to AI)',
                 children: (
                   <div style={{ background: '#0f172a', padding: 12, borderRadius: 8, color: '#f8fafc', fontSize: '12px' }}>
                     <pre style={{ marginBottom: 0 }}>{`curl -X POST "${API_ORIGIN}/api/inbound-routing" \\
  -H "X-API-Key: ${account?.apiKey?.substring(0, 5)}..." \\
  -H "Content-Type: application/json" \\
  -d '{ "phoneDigits": "918840824279", "mode": "ai" }'`}</pre>
                   </div>
                 )
               }
             ]} />
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', paddingBottom: 40 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2} style={{ margin: 0 }}><SettingOutlined /> Account Centre</Title>
          <Text type="secondary">Manage your identity, WhatsApp integrations, and CRM APIs.</Text>
        </div>
        <Button type="primary" size="large" icon={<SyncOutlined />} loading={saving} onClick={() => message.success('Changes auto-saved')}>
          Sync Changes
        </Button>
      </div>

      <Tabs defaultActiveKey="1" items={items} type="card" />

        {/* Number Configuration Modal */}
        <Modal
          title={editingNumber ? "Edit WhatsApp Number" : "Add New WhatsApp Number"}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setDiscoveredNumbers([]);
          }}
          footer={null}
          destroyOnClose
        >
          <Form 
            form={addForm}
            layout="vertical" 
            onFinish={handleModalOk}
            initialValues={editingNumber}
          >
            <Form.Item name="accessToken" label="1. Access Token (Meta System User Token)" rules={[{ required: true }]}>
              <Input.Password 
                placeholder="EAAb86..." 
                addonAfter={
                  <Button 
                    type="link" 
                    size="small" 
                    loading={discovering} 
                    onClick={handleDiscoverNumbers}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    <SyncOutlined /> Find My Numbers
                  </Button>
                }
              />
            </Form.Item>

            {discoveredNumbers.length > 0 && (
              <Form.Item label="Select Discovered Number">
                <Select 
                  placeholder="Found these numbers..." 
                  onChange={handleSelectDiscovered}
                  options={discoveredNumbers.map(n => ({
                    label: `${n.displayNumber} (${n.verifiedName})`,
                    value: n.wabaId
                  }))}
                />
              </Form.Item>
            )}

            <Divider orientation="left" plain>Manual IDs (Or Auto-filled)</Divider>

            <Alert
              message="How to get these IDs?"
              description={
                <div style={{ fontSize: '12px' }}>
                  1. Go to your <b>Meta Business Suite</b> → <b>Settings</b> → <b>WhatsApp Accounts</b>.<br/>
                  2. Select your account and find the <b>WABA ID</b> (usually 15-16 digits).<br/>
                  3. In your Meta App → <b>WhatsApp</b> → <b>Configuration</b>, find the <b>Phone Number ID</b>.<br/>
                  4. For the <b>System User Token</b>, go to <b>Business Settings</b> → <b>System Users</b> → <b>Generate Token</b> (Check <i>whatsapp_business_messaging</i> and <i>whatsapp_business_management</i>).
                </div>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form.Item name="displayNumber" label="Internal Name (Friendly)" rules={[{ required: true }]}>
              <Input placeholder="e.g. Primary WhatsApp" />
            </Form.Item>
            <Form.Item name="phoneNumberId" label="Phone Number ID" rules={[{ required: true }]}>
              <Input placeholder="997494506789050" />
            </Form.Item>
            <Form.Item name="wabaId" label="WABA ID" rules={[{ required: true }]}>
              <Input placeholder="1653378299124339" />
            </Form.Item>

            <Form.Item name="verifyToken" label="Webhook Verify Token" initialValue="ombrella_secret_123">
              <Input placeholder="Shared secret for Meta webhook" />
            </Form.Item>

            
            <div style={{ textAlign: 'right', marginTop: 24 }}>
              <Space>
                <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {editingNumber ? "Update Number" : "Connect Number"}
                </Button>
              </Space>
            </div>
          </Form>
        </Modal>

        {/* Advanced Test Modal */}
        <Modal
          title={`Send Test Message (${testNumber?.displayNumber})`}
          open={testModalVisible}
          onCancel={() => setTestModalVisible(false)}
          onOk={() => testForm.submit()}
          okText="Send Test"
          confirmLoading={testLoading[testNumber?.phoneNumberId]}
          destroyOnClose
        >
          <Form 
            form={testForm} 
            layout="vertical" 
            onFinish={handleSendAdvancedTest}
            initialValues={{ type: 'text' }}
          >
            <Form.Item name="recipient" label="Recipient Phone" rules={[{ required: true, message: 'Include country code, e.g. 91884...' }]}>
              <Input placeholder="Recipient Number (e.g. 918840824279)" />
            </Form.Item>
            
            <Form.Item name="type" label="Message Type">
              <Radio.Group buttonStyle="solid">
                <Radio.Button value="text">Session Text</Radio.Button>
                <Radio.Button value="template">Meta Template</Radio.Button>
              </Radio.Group>
            </Form.Item>

            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.type !== curr.type || prev.templateName !== curr.templateName}
            >
              {({ getFieldValue }) => {
                const isTemplate = getFieldValue('type') === 'template';
                const tplName = getFieldValue('templateName');
                const selectedTpl = remoteTemplates.find(t => t.name === tplName);
                
                if (!isTemplate) {
                  return (
                    <div style={{ marginTop: 16 }}>
                      <Alert 
                        message="Session Rule" 
                        description="Text messages only work if the recipient has messaged you in the last 24 hours." 
                        type="info" 
                        showIcon 
                      />
                    </div>
                  );
                }

                return (
                  <div style={{ marginTop: 16 }}>
                    <Form.Item name="templateName" label="Select Template" rules={[{ required: true }]}>
                      <Select 
                        placeholder={remoteTemplates.length > 0 ? "Pick an approved template" : "No templates found in Meta"}
                        loading={remoteTemplates.length === 0}
                        options={remoteTemplates.map(t => ({ label: `${t.name} (${t.language})`, value: t.name }))}
                        notFoundContent="No approved templates found."
                      />
                    </Form.Item>

                    {tplName && (
                      <>
                        <div style={{ background: '#f8fafc', padding: 12, borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 16 }}>
                          <Text type="secondary" style={{ fontSize: '11px' }}>TEMPLATE PREVIEW:</Text><br/>
                          <Text italic>"{selectedTpl?.components?.find(c => c.type === 'BODY')?.text || ''}"</Text>
                        </div>
                        
                        {/* Scan and show variables */}
                        {(() => {
                           const bodyText = selectedTpl?.components?.find(c => c.type === 'BODY')?.text || '';
                           const varMatches = bodyText.match(/{{(\d+)}}/g) || [];
                           const uniqueVars = [...new Set(varMatches.map(m => m.replace(/{{|}}/g, '')))].sort((a,b) => a-b);
                           
                           return uniqueVars.map(vNum => (
                             <Form.Item 
                                key={vNum}
                                name={['variables', vNum]} 
                                label={`Value for {{${vNum}}}`} 
                                rules={[{ required: true, message: 'Missing value' }]}
                             >
                               <Input placeholder={`Enter text for variable ${vNum}`} />
                             </Form.Item>
                           ));
                        })()}
                      </>
                    )}
                  </div>
                );
              }}
            </Form.Item>
          </Form>
        </Modal>
    </div>
  );
};

export default AccountManager;
